import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { PmDialogComponent } from '@libs/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { triggerLinkDownload } from 'common/export-helper';
import { ExportHttpClient } from 'demand-forecasting/services/http/export-http-client';
import { ToastrService } from 'ngx-toastr';
import { catchError, interval, map, takeWhile } from 'rxjs';

export type DownloadExportComponentData = {
  exportId: string;
};

@Component({
  selector: 'pm-download-export',
  template: `
    <pm-dialog
      dialogTitle="RESULT.KPIS.EXPORT_OPTIONS.DOWNLOAD_EXPORT"
      size="md"
      contentClass="gap-2 justify-center items-start"
      [primaryCtaButtonConfig]="primaryCtaButtonConfig"
    >
      @if (fileName) {
        <div class="flex flex-row gap-2 opacity-60 justify-center">
          <p>
            {{
              'RESULT.KPIS.EXPORT_OPTIONS.DOWNLOAD_STARTED_SUCESSFULLY_FOR'
                | translate
            }}
          </p>
        </div>
        <p class="italic">
          {{ fileName }}
        </p>

        @if (downloadAgainCountdown > 0) {
          <div class="flex flex-col gap-2 mt-4 text-end w-full">
            <mat-divider class="w-full"></mat-divider>
            <p class="text-xs">
              {{
                'RESULT.KPIS.EXPORT_OPTIONS.IN_X_SECONDS_YOU_CAN_START_DOWNLOAD_AGAIN'
                  | translate
                    : {
                        seconds: downloadAgainCountdown,
                      }
              }}
            </p>
          </div>
        }
      } @else {
        <div class="flex flex-row gap-2 justify-center items-center">
          <p class="opacity-60">
            {{ 'RESULT.KPIS.EXPORT_OPTIONS.DOWNLOAD_STARTS' | translate }}
          </p>
          <mat-spinner [diameter]="18" />
        </div>
      }
    </pm-dialog>
  `,
  imports: [
    PmDialogComponent,
    TranslateModule,
    MatProgressSpinner,
    MatDividerModule,
  ],
})
export class DownloadExportDialogComponent implements OnInit {
  data = inject<DownloadExportComponentData>(MAT_DIALOG_DATA);

  private readonly COUNTDOWN_DURATION_IN_SECONDS = 10;

  private readonly exportHttpClient = inject(ExportHttpClient);

  private readonly toastService = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(
    MatDialogRef<DownloadExportDialogComponent>,
  );
  private readonly router = inject(Router);

  protected fileName: string | undefined;

  protected downloadAgainCountdown = 0;

  protected get primaryCtaButtonConfig() {
    return {
      text: 'RESULT.KPIS.EXPORT_OPTIONS.DOWNLOAD_AGAIN',
      disabled: !this.fileName || this.downloadAgainCountdown > 0,
      dataTestid: 'download-export-again-button',
      handleClick: () => {
        this.triggerDownload();
        this.dialogRef.close();
      },
    };
  }

  ngOnInit(): void {
    this.triggerDownload();

    // remove exportId from url when dialog is closed
    this.dialogRef.afterClosed().subscribe(() => {
      this.router.navigate([], {
        queryParams: {
          exportId: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected triggerDownload() {
    this.exportHttpClient
      .getDownloadData(this.data.exportId)
      .pipe(
        map((res) => {
          this.fileName = res.fileName;
          this.startCountdown();
          triggerLinkDownload(res.downloadLink, res.fileName);
        }),
        catchError((error) => {
          console.error('Error while fetching download data', error);
          this.toastService.error(
            this.translate.instant(
              'RESULT.KPIS.EXPORT_OPTIONS.PLEASE_CONTACT_SUPPORT',
            ),
            this.translate.instant(
              'RESULT.KPIS.EXPORT_OPTIONS.ERROR_WHILE_STARTING_DOWNLOAD',
            ),
          );
          return error;
        }),
      )
      .subscribe();
  }

  private startCountdown(): void {
    this.downloadAgainCountdown = this.COUNTDOWN_DURATION_IN_SECONDS;
    interval(1000)
      .pipe(takeWhile(() => this.downloadAgainCountdown > 0))
      .subscribe(() => {
        this.downloadAgainCountdown -= 1;
      });
  }
}
